import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, RefreshCw, Sparkles } from 'lucide-react';
import {
  Callout,
  SegmentedControl,
  WorkspaceBanner,
  WorkspaceButton,
} from '@/components/ui/workspace';
import { ErrorMessage } from '@/components/ui/error-message';
import { StatusChip } from '@/components/ui/status-chip';
import { Spinner } from '@/components/ui/spinner';
import { ApiError } from '@/lib/api/client';
import {
  processMapKeys,
  startFutureDraft,
  useFutureProductsQuery,
  type FutureMapSummary,
  type FutureMetricsDto,
  type FutureProduct,
  type MapCompare,
  type MapCompareTable,
  type MapProcess,
  type MapStepDto,
  type VendorInfoChange,
} from '@/lib/api/process-maps';
import { cn } from '@/lib/utils/cn';
import { MapCanvas } from './map-canvas';
import { COMPARE_COLUMNS, affectedSteps } from './future-logic';
import { plural, type Markers } from './map-logic';
import { Swatch } from './map-parts';

/**
 * The future-state parts of Discovery -> Process maps (T-0208, E13-F09;
 * prototype `mapsView()` with `S.map.mode === 'future'`). They sit above,
 * below or beside the canvas, never over it (OQ-V), and show what the
 * server computed: tags, counts and newer vendor information (rule 26).
 */

export type MapState = 'current' | 'future';
export type FutureLayout = 'map' | 'compare';

/** "Current state | Future state", at the start of the map toolbar. */
export function MapStateSwitch({
  value,
  onChange,
}: {
  value: MapState;
  onChange: (value: MapState) => void;
}) {
  return (
    <SegmentedControl
      aria-label="Map state"
      size="sm"
      value={value}
      onChange={onChange}
      options={[
        { value: 'current', label: 'Current state' },
        { value: 'future', label: 'Future state' },
      ]}
    />
  );
}

/** "with [product]": the process's future maps, and another product to draw one with. */
export function ProductSelect({
  futures,
  value,
  onChange,
}: {
  futures: FutureMapSummary[];
  /** A vendor product id, or 'choose' for the picker. */
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="inline-flex items-center gap-1.5 text-meta text-ink-500">
      with
      <select
        aria-label="Vendor product"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-[26px] max-w-[260px] rounded-card border border-ink-200 bg-paper-0 px-2 text-body-sm text-ink-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
      >
        {futures.map((f) => (
          <option key={f.map_id} value={f.vendor_product.id}>
            {f.vendor_product.name}
          </option>
        ))}
        <option value="choose">Another product…</option>
      </select>
    </label>
  );
}

export function LayoutSwitch({
  value,
  onChange,
}: {
  value: FutureLayout;
  onChange: (value: FutureLayout) => void;
}) {
  return (
    <SegmentedControl
      aria-label="Layout"
      size="sm"
      value={value}
      onChange={onChange}
      options={[
        { value: 'map', label: 'Map' },
        { value: 'compare', label: 'Compare with today' },
      ]}
    />
  );
}

/** What the marks on a future map mean (the prototype's tags, drawn on the shapes). */
export function FutureLegend({
  layout,
  previewing,
  hint,
}: {
  layout: FutureLayout;
  previewing: boolean;
  hint: string;
}) {
  const item = (cls: string, label: string) => (
    <span key={label} className="inline-flex items-center gap-1.5">
      <Swatch className={cls} />
      {label}
    </span>
  );
  return (
    <div
      data-testid="future-legend"
      className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 pt-2.5 text-meta text-ink-500"
    >
      {item('border-ink-700 bg-paper-0', 'Unchanged')}
      {item('border-accent-amber-600 bg-paper-0', 'Changed')}
      {item('border-posture-healthy bg-posture-healthy-tint', 'Automated')}
      {item('border-posture-healthy bg-paper-0', 'New')}
      {item('border-posture-critical bg-paper-0', 'Workaround')}
      {item('border-dashed border-ink-700 bg-paper-0', 'Assumption: no vendor evidence')}
      {item('border-dotted border-accent-amber-700 bg-paper-0', 'Newer vendor information')}
      {layout === 'compare' ? item('border-dashed border-ink-500 bg-paper-100', 'Removed') : null}
      {previewing ? (
        <>
          {item('border-dashed border-accent-amber-600 bg-accent-amber-50', 'proposed change')}
          {item('border-posture-healthy bg-posture-healthy-tint', 'proposed new step')}
        </>
      ) : null}
      <span className="min-w-0 flex-1 text-right max-[900px]:text-left">{hint}</span>
    </div>
  );
}

/**
 * Today and each product's latest future version (`GET /compare-table`),
 * counted by code on each map's main path (rule 26). The future state on
 * screen is highlighted.
 */
export function ComparisonTable({
  table,
  error,
  shownMapId,
  evidence,
  note,
}: {
  table: MapCompareTable | undefined;
  error: unknown;
  shownMapId: string;
  /** The map on screen's evidence counts, as the server counted them. */
  evidence?: { product: string; versionNo: number; metrics: FutureMetricsDto } | null;
  /** Why the counts may differ from the map on screen (a change shown, an older version). */
  note?: string | null;
}) {
  if (error) return <ErrorMessage error={error} />;
  if (!table) {
    return (
      <div className="flex items-center gap-2 text-meta text-ink-500">
        <Spinner /> Counting…
      </div>
    );
  }
  return (
    <div data-testid="future-compare-table" className="min-w-0">
      <div className="max-w-full overflow-x-auto">
        <table className="border-collapse text-[12px]">
          <caption className="sr-only">Counted from the maps</caption>
          <thead>
            <tr>
              <th
                scope="col"
                className="whitespace-nowrap py-[3px] pl-1.5 pr-3.5 text-left align-bottom font-medium text-ink-500"
              >
                Counted from the maps
              </th>
              {COMPARE_COLUMNS.map((c) => (
                <th
                  key={c.label}
                  scope="col"
                  className="min-w-[72px] max-w-[132px] py-[3px] pr-3.5 text-right align-bottom font-medium leading-4 text-ink-500"
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((r) => {
              const hl = r.map_id === shownMapId && r.kind === 'future';
              return (
                <tr
                  key={r.map_id}
                  aria-current={hl ? 'true' : undefined}
                  className={cn(hl && 'bg-accent-amber-50 font-bold')}
                >
                  <th
                    scope="row"
                    className="whitespace-nowrap py-[3px] pl-1.5 pr-3.5 text-left font-[inherit] text-ink-900"
                  >
                    {r.kind === 'today'
                      ? `Today (v${r.version_no})`
                      : `${r.vendor_product.name} (v${r.version_no})`}
                  </th>
                  {COMPARE_COLUMNS.map((c) => {
                    const v = c.value(r);
                    return (
                      <td
                        key={c.label}
                        className="whitespace-nowrap py-[3px] pr-3.5 text-right tabular-nums text-ink-900"
                      >
                        {v === null ? <span className="text-ink-500">—</span> : v}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {evidence ? (
        <p data-testid="future-evidence-counts" className="mt-1.5 text-body-sm text-ink-700">
          {evidence.product} v{evidence.versionNo}:{' '}
          <b className="font-semibold tabular-nums text-ink-900">{evidence.metrics.vendor_cited}</b>{' '}
          {evidence.metrics.vendor_cited === 1 ? 'step cites' : 'steps cite'} vendor evidence ·{' '}
          <b className="font-semibold tabular-nums text-ink-900">{evidence.metrics.assumptions}</b>{' '}
          {evidence.metrics.assumptions === 1 ? 'assumption' : 'assumptions'} ·{' '}
          <b className="font-semibold tabular-nums text-ink-900">{evidence.metrics.removed}</b>{' '}
          {evidence.metrics.removed === 1 ? 'step' : 'steps'} of today removed
        </p>
      ) : null}
      <p className="mt-1 text-meta text-ink-500">
        Counted by code on each map&rsquo;s main path. Cost and time savings are not estimated.
        {note ? ` ${note}` : ''}
      </p>
    </div>
  );
}

function StartShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-1 items-start justify-center overflow-y-auto p-8">
      <div className="flex w-full max-w-[720px] flex-col gap-3">{children}</div>
    </div>
  );
}

/** A future state is drawn from a validated current state only (Decision 62). */
export function FutureNotReady({ process, onBack }: { process: MapProcess; onBack: () => void }) {
  const v = process.map?.latest_version_no ?? 1;
  const waiting = process.map?.validation_state === 'requested';
  return (
    <StartShell>
      <StatusChip tone="unassessed" className="self-start">
        Not ready
      </StatusChip>
      <h2 className="text-h2 font-semibold text-ink-900">
        The future state needs a validated current state
      </h2>
      <p className="text-body-sm text-ink-700">
        AI drafts a future state for a vendor product from the current state the client validated,
        so it is drawn against what they agreed.{' '}
        {waiting
          ? `Version ${v} of the current state is waiting for the client.`
          : `Version ${v} of the current state is not validated yet. Validate it with the client first.`}
      </p>
      <div>
        <WorkspaceButton onClick={onBack}>Back to the current state</WorkspaceButton>
      </div>
    </StartShell>
  );
}

function startError(err: unknown): string {
  if (err instanceof ApiError) {
    const reason = err.details?.['reason'];
    if (reason === 'map.current_not_validated')
      return 'The current state is not validated, so there is nothing to draw the future state from yet.';
    if (reason === 'map.product_not_available')
      return 'That product is retired or has no confirmed knowledge items, so it cannot be drawn.';
    if (reason === 'ai.budget_exhausted')
      return "This engagement's AI budget is used up, so nothing was drafted.";
    return err.message;
  }
  return err instanceof Error ? err.message : 'That did not work.';
}

/**
 * The product picker (`GET /future-products`): products with confirmed items
 * in the area's library modules first, counted by code; choosing one asks
 * AI to draft the future state with it.
 */
export function FuturePicker({
  engagementId,
  process,
  futures,
  onOpen,
}: {
  engagementId: string;
  process: MapProcess;
  futures: FutureMapSummary[];
  /** Show the future state with this product (drafting, or drawn). */
  onOpen: (productId: string) => void;
}) {
  const qc = useQueryClient();
  const q = useFutureProductsQuery(engagementId, process.process_id, true);
  const [busy, setBusy] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const drawn = new Set(futures.map((f) => f.vendor_product.id));

  const draft = async (p: FutureProduct) => {
    setBusy(p.product_id);
    setError(null);
    try {
      await startFutureDraft(engagementId, process.process_id, p.product_id);
      await qc.invalidateQueries({ queryKey: processMapKeys.processes(engagementId) });
      onOpen(p.product_id);
    } catch (err) {
      const reason = err instanceof ApiError ? err.details?.['reason'] : null;
      if (reason === 'map.future_exists' || reason === 'map.draft_in_progress') {
        await qc.invalidateQueries({ queryKey: processMapKeys.processes(engagementId) });
        onOpen(p.product_id);
      } else setError(startError(err));
    } finally {
      setBusy(null);
    }
  };

  const products = q.data ?? [];
  const firstWithout = products.findIndex((p) => p.confirmed_items === 0);
  return (
    <StartShell>
      <h2 className="text-h2 font-semibold text-ink-900">
        Draw the future state of {process.name} with a vendor product
      </h2>
      <p className="text-body-sm text-ink-700">
        AI drafts it from the validated current state (version{' '}
        {process.current_validated_version_no}), this area&rsquo;s requirements and the
        product&rsquo;s confirmed knowledge items. Every step that says the product does something
        cites an item, or is marked as an assumption. You see it against today before anything is
        shared.
      </p>
      {error ? (
        <Callout tone="warn" icon={AlertTriangle}>
          {error}
        </Callout>
      ) : null}
      {q.error ? <ErrorMessage error={q.error} /> : null}
      {q.isPending ? (
        <div className="flex items-center gap-2 text-body-sm text-ink-500">
          <Spinner /> Loading products…
        </div>
      ) : null}
      {q.data && products.length === 0 ? (
        <p className="text-body-sm text-ink-500">
          No vendor product has confirmed knowledge items yet. Add them in the knowledge base first.
        </p>
      ) : null}
      {products.length > 0 ? (
        <ul aria-label="Vendor products" className="rounded-card border border-ink-100">
          {products.map((p, i) => {
            const has = drawn.has(p.product_id);
            return (
              <React.Fragment key={p.product_id}>
                {i === firstWithout ? (
                  <li className="border-b border-ink-100 bg-paper-50 px-3.5 py-1.5 text-meta font-semibold uppercase tracking-[0.06em] text-ink-500">
                    No items in this area&rsquo;s modules yet
                  </li>
                ) : null}
                <li className="flex flex-wrap items-center gap-3 border-b border-ink-100 px-3.5 py-2.5 last:border-b-0">
                  <span className="min-w-0 flex-1">
                    <span className="block text-body-sm font-semibold text-ink-900">{p.name}</span>
                    <span className="block text-meta text-ink-500">
                      {p.vendor.name} · {plural(p.confirmed_items, 'confirmed item')} in this area ·{' '}
                      {plural(p.answers_to_engagement_requirements, 'answer')} to this
                      engagement&rsquo;s requirements
                    </span>
                  </span>
                  {has ? (
                    <WorkspaceButton onClick={() => onOpen(p.product_id)}>Open</WorkspaceButton>
                  ) : (
                    <WorkspaceButton
                      variant={p.confirmed_items > 0 ? 'primary' : 'secondary'}
                      disabled={busy !== null}
                      aria-label={`Draft the future state with ${p.name}`}
                      onClick={() => void draft(p)}
                    >
                      <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                      {busy === p.product_id ? 'Starting…' : 'Draft with AI'}
                    </WorkspaceButton>
                  )}
                </li>
              </React.Fragment>
            );
          })}
        </ul>
      ) : null}
    </StartShell>
  );
}

/** A future map whose first draft is running, or failed (nothing was kept). */
export function FutureDrafting({
  engagementId,
  process,
  future,
}: {
  engagementId: string;
  process: MapProcess;
  future: FutureMapSummary;
}) {
  const qc = useQueryClient();
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const product = future.vendor_product;
  const failed = future.draft?.status === 'failed';

  const retry = async () => {
    setBusy(true);
    setError(null);
    try {
      await startFutureDraft(engagementId, process.process_id, product.id);
    } catch (err) {
      if (!(err instanceof ApiError && err.details?.['reason'] === 'map.draft_in_progress'))
        setError(startError(err));
    } finally {
      setBusy(false);
      await qc.invalidateQueries({ queryKey: processMapKeys.processes(engagementId) });
    }
  };

  return (
    <StartShell>
      <StatusChip tone={failed ? 'at-risk' : 'ai'} className="self-start">
        {failed ? 'Draft failed' : 'Drafting'}
      </StatusChip>
      <h2 className="text-h2 font-semibold text-ink-900">
        {process.name} with {product.name}
      </h2>
      {failed ? (
        <>
          <Callout tone="warn" icon={AlertTriangle}>
            The draft did not finish
            {future.draft?.message ? `: ${future.draft.message}` : '.'} Nothing was kept.
          </Callout>
          {error ? (
            <Callout tone="warn" icon={AlertTriangle}>
              {error}
            </Callout>
          ) : null}
          <div>
            <WorkspaceButton variant="primary" disabled={busy} onClick={() => void retry()}>
              Try the draft again
            </WorkspaceButton>
          </div>
        </>
      ) : (
        <p className="flex items-start gap-2 text-body-sm text-ink-700">
          <Spinner />
          <span>
            AI is drafting the future state with {product.name} from the validated current state
            {process.current_validated_version_no
              ? ` (version ${process.current_validated_version_no})`
              : ''}
            , this area&rsquo;s requirements and {product.name}&rsquo;s confirmed knowledge items.
            Each step it says the product does cites an item or is marked as an assumption. It shows
            here when it is done.
          </span>
        </p>
      )}
    </StartShell>
  );
}

/**
 * "Newer vendor information for N steps" (rule 25): what the map cites was
 * superseded, retired or flagged since. The map keeps citing it until a
 * redraft is applied; nothing changes by itself.
 */
export function VendorInfoLine({
  changes,
  steps,
  disabledReason,
  busy,
  onRedraft,
}: {
  changes: VendorInfoChange[];
  steps: ReadonlyArray<Pick<MapStepDto, 'key' | 'name'>>;
  disabledReason: string | null;
  busy: boolean;
  onRedraft: () => void;
}) {
  const affected = affectedSteps(changes, steps);
  if (affected.length === 0) return null;
  return (
    <div className="px-4 pt-2">
      <WorkspaceBanner
        actions={
          <WorkspaceButton
            disabled={busy || disabledReason !== null}
            title={disabledReason ?? undefined}
            onClick={onRedraft}
          >
            <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
            Redraft affected steps
          </WorkspaceButton>
        }
      >
        <strong className="font-semibold text-ink-900">
          Newer vendor information for {plural(affected.length, 'step')}:
        </strong>{' '}
        {affected.map((s) => `“${s.name}”`).join(', ')}. The map still cites the versions it was
        drawn with; a redraft is shown on the map and changes nothing until you apply it.
      </WorkspaceBanner>
    </div>
  );
}

/** "Drawn against current state vN; vM is now validated" (Decision 62), with Redraw. */
export function RedrawLine({
  drawnAgainst,
  validatedNo,
  redrawing,
  disabledReason,
  busy,
  onRedraw,
}: {
  drawnAgainst: number;
  validatedNo: number;
  redrawing: boolean;
  disabledReason: string | null;
  busy: boolean;
  onRedraw: () => void;
}) {
  return (
    <div className="px-4 pt-2">
      <WorkspaceBanner
        actions={
          redrawing ? null : (
            <WorkspaceButton
              disabled={busy || disabledReason !== null}
              title={disabledReason ?? undefined}
              onClick={onRedraw}
            >
              <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
              Redraw from v{validatedNo}
            </WorkspaceButton>
          )
        }
      >
        {redrawing ? (
          <span className="inline-flex items-center gap-2">
            <Spinner /> AI is redrawing this future state from current state v{validatedNo}. It
            shows as the next version when it is done.
          </span>
        ) : (
          <>
            <strong className="font-semibold text-ink-900">
              Drawn against current state v{drawnAgainst}; v{validatedNo} is now validated.
            </strong>{' '}
            Redraw writes the next version from it. This version stays in the history.
          </>
        )}
      </WorkspaceBanner>
    </div>
  );
}

/**
 * Compare with today: the current state the future version was drawn
 * against beside it, each on its own canvas. Selecting a step on one side
 * selects its pair on the other (same key, ADR-020).
 */
export function CompareCanvases({
  compare,
  productName,
  processName,
  todayMarkers,
  futureMarkers,
  onSelectToday,
  onSelectFuture,
}: {
  compare: MapCompare;
  productName: string;
  processName: string;
  todayMarkers: Markers;
  futureMarkers: Markers;
  onSelectToday: (elementId: string) => void;
  onSelectFuture: (elementId: string) => void;
}) {
  return (
    // No min-h-0: the two canvases keep their height and the comparison table
    // scrolls in below them instead of being drawn under them (T-0192).
    <div className="flex flex-1 max-[1100px]:flex-col">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div className="px-4 pt-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-500">
          Today · current state v{compare.today.version_no}
        </div>
        <MapCanvas
          xml={compare.today.bpmn_xml}
          markers={todayMarkers}
          editable={false}
          label={`${processName}, today, current-state version ${compare.today.version_no}`}
          onSelect={onSelectToday}
        />
      </div>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div className="px-4 pt-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-500">
          With {productName} · v{compare.future.version_no}
        </div>
        <MapCanvas
          xml={compare.future.bpmn_xml}
          markers={futureMarkers}
          editable={false}
          label={`${processName} with ${productName}, future-state version ${compare.future.version_no}`}
          onSelect={onSelectFuture}
        />
      </div>
    </div>
  );
}
